import { useState, useEffect } from "react";
import { PenNib, Envelope, MapPin, CalendarBlank, FileCsv, FilePdf, PaperPlaneTilt, Trash } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 200;

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

function initials(name) {
  return (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

const TINTS = [
  { bg: "rgba(26,92,55,0.1)", color: "#1A5C37" },
  { bg: "rgba(184,92,56,0.1)", color: "#B85C38" },
  { bg: "rgba(74,84,80,0.12)", color: "#4A5450" },
  { bg: "rgba(26,92,55,0.15)", color: "#14482B" },
  { bg: "rgba(184,92,56,0.15)", color: "#964B2E" },
];

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function csvCell(value) {
  const text = value == null ? "" : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

function pdfText(value) {
  return String(value ?? "")
    .replace(/[^\x20-\x7E]/g, " ")
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");
}

function wrapText(text, maxChars = 84) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let current = "";
  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > maxChars && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });
  if (current) lines.push(current);
  return lines.length ? lines : [""];
}

function createPetitionPdf(signatures) {
  const lines = [
    { text: "KYBIA Petition Signatures", size: 18 },
    { text: "Citizens' Petition: Save Bellandur Lake", size: 12 },
    { text: `Exported: ${new Date().toLocaleString("en-IN")}`, size: 10 },
    { text: `Total signatures: ${signatures.length}`, size: 10 },
    { text: "", size: 10 },
  ];

  signatures.forEach((sig, idx) => {
    lines.push({ text: `${idx + 1}. ${sig.name || ""}`, size: 11 });
    lines.push({ text: `Email: ${sig.email || ""}`, size: 10 });
    if (sig.address) lines.push({ text: `Address: ${sig.address}`, size: 10 });
    if (sig.mobile) lines.push({ text: `Mobile: ${sig.mobile}`, size: 10 });
    lines.push({ text: `Signed: ${formatDate(sig.created_at)}`, size: 10 });
    lines.push({ text: "", size: 10 });
  });

  const pages = [];
  let pageLines = [];
  let y = 760;
  lines.forEach((entry) => {
    wrapText(entry.text).forEach((line) => {
      if (y < 64) {
        pages.push(pageLines);
        pageLines = [];
        y = 760;
      }
      pageLines.push({ text: line, size: entry.size, y });
      y -= entry.size + 6;
    });
  });
  if (pageLines.length) pages.push(pageLines);

  const objects = [];
  const addObject = (body) => {
    objects.push(body);
    return objects.length;
  };

  const fontId = addObject("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>");
  const pageIds = [];
  const contentIds = [];
  pages.forEach((page) => {
    const stream = page.map((line) => `BT /F1 ${line.size} Tf 48 ${line.y} Td (${pdfText(line.text)}) Tj ET`).join("\n");
    contentIds.push(addObject(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`));
  });
  const pagesId = objects.length + contentIds.length + 1;
  contentIds.forEach((contentId) => {
    pageIds.push(addObject(`<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 ${fontId} 0 R >> >> /Contents ${contentId} 0 R >>`));
  });
  const pagesObjectId = addObject(`<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pageIds.length} >>`);
  const catalogId = addObject(`<< /Type /Catalog /Pages ${pagesObjectId} 0 R >>`);

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  objects.forEach((body, idx) => {
    offsets.push(pdf.length);
    pdf += `${idx + 1} 0 obj\n${body}\nendobj\n`;
  });
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  return new Blob([pdf], { type: "application/pdf" });
}

export default function AdminPetitions() {
  const [signatures, setSignatures] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [actionMessage, setActionMessage] = useState("");

  useEffect(() => {
    Promise.all([
      fetch(`${BACKEND_URL}/api/petition-signatures?page=1&limit=${PAGE_SIZE}`),
      fetch(`${BACKEND_URL}/api/petition-signatures/count`),
    ])
      .then(async ([signaturesResponse, countResponse]) => {
        if (!signaturesResponse.ok || !countResponse.ok) throw new Error("Load failed");
        const [signatureData, countData] = await Promise.all([
          signaturesResponse.json(),
          countResponse.json(),
        ]);
        setSignatures(Array.isArray(signatureData) ? signatureData : []);
        setTotalCount(Number(countData?.count) || 0);
      })
      .catch(() => {
        setSignatures([]);
        setTotalCount(0);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleLoadMore = async () => {
    const nextPage = page + 1;
    setLoadingMore(true);
    setActionMessage("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/petition-signatures?page=${nextPage}&limit=${PAGE_SIZE}`);
      if (!res.ok) throw new Error("Load failed");
      const data = await res.json();
      if (!Array.isArray(data)) throw new Error("Invalid response");
      setSignatures((current) => [...current, ...data]);
      setPage(nextPage);
    } catch {
      setActionMessage("More petition signatures could not be loaded.");
    } finally {
      setLoadingMore(false);
    }
  };

  const exportCsv = () => {
    const headers = ["Name", "Email", "Address", "Mobile", "Signed At"];
    const rows = signatures.map((sig) => [
      sig.name,
      sig.email,
      sig.address,
      sig.mobile,
      sig.created_at ? new Date(sig.created_at).toLocaleString("en-IN") : "",
    ]);
    const csv = `\uFEFF${[headers, ...rows].map((row) => row.map(csvCell).join(",")).join("\n")}`;
    downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), "petition-signatures.csv");
  };

  const exportPdf = () => {
    downloadBlob(createPetitionPdf(signatures), "petition-signatures.pdf");
  };

  const handleSendMail = async (signatureId) => {
    setSendingId(signatureId);
    setActionMessage("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/petition-signatures/${signatureId}/send-mail`, { method: "POST" });
      if (!res.ok) throw new Error("Send failed");
      setActionMessage("Mail sent successfully.");
    } catch {
      setActionMessage("Mail could not be sent. Check mail settings.");
    } finally {
      setSendingId(null);
    }
  };

  const handleDelete = async (signatureId) => {
    if (!window.confirm("Delete this petition signature?")) return;
    setDeletingId(signatureId);
    setActionMessage("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/petition-signatures/${signatureId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed");
      setSignatures((current) => current.filter((sig) => sig.id !== signatureId));
      setTotalCount((current) => Math.max(0, current - 1));
      setActionMessage("Petition signature deleted.");
    } catch {
      setActionMessage("Petition signature could not be deleted.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div>
          <h3 className="font-heading text-xl font-medium" style={{ color: "#1C211F" }}>Petitions</h3>
          <p className="text-sm mt-0.5" style={{ color: "#4A5450" }}>
            Showing {signatures.length} of {totalCount} signature{totalCount !== 1 ? "s" : ""}, latest first — Citizens&apos; Petition: Save Bellandur Lake
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={exportCsv}
            disabled={loading || signatures.length === 0}
            className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium border transition-colors"
            style={{ color: "#1A5C37", borderColor: "rgba(26,92,55,0.25)", opacity: loading || signatures.length === 0 ? 0.45 : 1 }}
          >
            <FileCsv size={16} weight="duotone" />
            Export CSV
          </button>
          <button
            type="button"
            onClick={exportPdf}
            disabled={loading || signatures.length === 0}
            className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: "#1A5C37", color: "#F7F5F0", opacity: loading || signatures.length === 0 ? 0.45 : 1 }}
          >
            <FilePdf size={16} weight="duotone" />
            Export PDF
          </button>
        </div>
      </div>

      {actionMessage && (
        <p className="text-xs px-3 py-2 rounded-lg mb-4" style={{ color: "#1A5C37", backgroundColor: "rgba(26,92,55,0.08)" }}>
          {actionMessage}
        </p>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: "#1A5C37", borderTopColor: "transparent" }} />
        </div>
      ) : signatures.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: "rgba(26,92,55,0.08)" }}>
            <PenNib size={28} weight="duotone" style={{ color: "#1A5C37" }} />
          </div>
          <p className="text-sm font-medium" style={{ color: "#1C211F" }}>No signatures yet</p>
          <p className="text-xs mt-1" style={{ color: "#4A5450" }}>
            Signatures from the Petition page will appear here.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
            {signatures.map((sig, idx) => {
            const tint = TINTS[idx % TINTS.length];
            const address = sig.address || "";
            return (
              <div
                key={sig.id}
                className="flex flex-col p-4 rounded-xl border"
                style={{ backgroundColor: "#fff", borderColor: "rgba(26,92,55,0.1)" }}
              >
                {/* Avatar + name + date */}
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold"
                    style={{ backgroundColor: tint.bg, color: tint.color }}
                  >
                    {initials(sig.name)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: "#1C211F" }}>{sig.name}</p>
                    <p className="flex items-center gap-1 text-xs" style={{ color: "#7A8A84" }}>
                      <CalendarBlank size={10} weight="duotone" />
                      {formatDate(sig.created_at)}
                    </p>
                  </div>
                </div>

                {/* Email */}
                <a
                  href={`mailto:${sig.email}`}
                  className="flex items-center gap-1.5 text-xs mb-1.5 truncate"
                  style={{ color: "#4A5450" }}
                >
                  <Envelope size={12} weight="duotone" style={{ color: "#1A5C37", flexShrink: 0 }} />
                  <span className="truncate">{sig.email}</span>
                </a>

                {/* Address */}
                {address && (
                  <p
                    className="flex items-start gap-1.5 text-xs leading-relaxed"
                    style={{ color: "#4A5450" }}
                  >
                    <MapPin size={12} weight="duotone" style={{ color: "#1A5C37", flexShrink: 0, marginTop: 2 }} />
                    <span>{address}</span>
                  </p>
                )}

                <div className="flex items-center gap-2 mt-3 pt-3 border-t" style={{ borderColor: "rgba(26,92,55,0.08)" }}>
                  <button
                    type="button"
                    onClick={() => handleSendMail(sig.id)}
                    disabled={sendingId === sig.id || deletingId === sig.id}
                    className="flex-1 flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-lg text-xs font-medium border transition-colors"
                    style={{ color: "#1A5C37", borderColor: "rgba(26,92,55,0.25)", opacity: sendingId === sig.id ? 0.6 : 1 }}
                  >
                    <PaperPlaneTilt size={13} weight="duotone" />
                    {sendingId === sig.id ? "Sending..." : "Send mail"}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(sig.id)}
                    disabled={deletingId === sig.id || sendingId === sig.id}
                    className="flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-lg text-xs font-medium border transition-colors"
                    style={{ color: "#B85C38", borderColor: "rgba(184,92,56,0.25)", opacity: deletingId === sig.id ? 0.6 : 1 }}
                  >
                    <Trash size={13} weight="duotone" />
                    {deletingId === sig.id ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            );
            })}
          </div>
          {signatures.length < totalCount && (
            <div className="flex flex-col items-center gap-2 mt-8">
              <button
                type="button"
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="px-5 py-2.5 rounded-lg text-sm font-medium transition-colors"
                style={{ backgroundColor: "#1A5C37", color: "#F7F5F0", opacity: loadingMore ? 0.6 : 1 }}
              >
                {loadingMore ? "Loading..." : `Load ${Math.min(PAGE_SIZE, totalCount - signatures.length)} more`}
              </button>
              <p className="text-xs" style={{ color: "#7A8A84" }}>
                {signatures.length} of {totalCount} signatures shown
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
